import { useEffect, useState, useCallback } from 'react';
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Loader2, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

/**
 * DutiLyne platform-staff widget (Platform console only — pharmacy owners
 * must never see vendor error noise). Surfaces self-detected errors captured
 * by the server-side error reporter: /admin/error-reports/stats for a compact
 * summary, with a "View details" panel that drills into the raw audit trail.
 */
export default function SystemHealthWidget() {
  const { user } = useAuth();
  const isPlatformStaff = user?.platform_owner === true || user?.role === 'platform_owner';

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillOpen, setDrillOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/error-reports/stats');
      setStats(data);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPlatformStaff) return;
    refresh();
    // Auto-refresh every 60s while the dashboard is open so new bugs
    // roll in without an F5. Pause polling while the drill modal is
    // open to avoid yanking the list out from under the admin.
    const id = setInterval(() => {
      if (!drillOpen) refresh();
    }, 60_000);
    return () => clearInterval(id);
  }, [isPlatformStaff, drillOpen, refresh]);

  const openDrill = async () => {
    setDrillOpen(true);
    setDrillLoading(true);
    try {
      const { data } = await api.get('/admin/error-reports?limit=50');
      setEntries(data?.entries || []);
    } catch (e) {
      setEntries([]);
    } finally {
      setDrillLoading(false);
    }
  };

  // Show only the path part of a URL (drop protocol + host) so the modal
  // doesn't truncate mid-word with long preview hostnames.
  const prettyUrl = (raw) => {
    if (!raw) return '';
    try {
      const u = new URL(raw);
      return u.pathname + (u.search || '');
    } catch {
      return raw.slice(0, 90);
    }
  };

  if (!isPlatformStaff) return null;

  const total24h = stats?.total_24h || 0;
  const total7d = stats?.total_7d || 0;
  const healthy = !loading && total24h === 0;
  const warning = !loading && total24h > 0 && total24h <= 5;
  const critical = !loading && total24h > 5;

  const tone = critical
    ? { ring: 'border-rose-200', bg: 'bg-rose-50/70', icon: 'bg-rose-100 text-rose-600', badge: 'bg-rose-100 text-rose-700 border-rose-200' }
    : warning
    ? { ring: 'border-amber-200', bg: 'bg-amber-50/70', icon: 'bg-amber-100 text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' }
    : { ring: 'border-emerald-200', bg: 'bg-emerald-50/70', icon: 'bg-emerald-100 text-emerald-600', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' };

  const statusLabel = loading ? 'Scanning…' : healthy ? 'All clear' : critical ? 'Action required' : 'Minor issues';

  return (
    <>
      <div
        data-testid="system-health-widget"
        className={`relative overflow-hidden rounded-2xl border p-5 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.08)] animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out ${tone.bg} ${tone.ring}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${tone.icon}`}>
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : healthy ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : critical ? (
                <AlertTriangle className="h-5 w-5" />
              ) : (
                <Activity className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Outfit' }}>
                  System Health
                </h3>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tone.badge}`}
                  data-testid="system-health-status-badge"
                >
                  {statusLabel}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                {loading
                  ? 'Reading the auto-detection log…'
                  : healthy
                  ? 'No bugs reported in the last 24 hours. The self-healing reporter is silent.'
                  : `${total24h} error${total24h === 1 ? '' : 's'} captured in the last 24h · ${total7d} in the last 7 days. Developer notified by email automatically.`}
              </p>

              {!loading && (stats?.by_source?.length || 0) > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="system-health-sources">
                  {stats.by_source.slice(0, 4).map((s) => (
                    <li
                      key={s.source}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-white/80 border border-slate-200 text-slate-700 font-mono"
                    >
                      {s.source.replace('frontend.', 'fe.').replace('backend.', 'be.')} · {s.count}
                    </li>
                  ))}
                </ul>
              )}

              {!loading && (stats?.top?.length || 0) > 0 && (
                <div className="mt-3 text-[11px] text-slate-500">
                  Top recurring:{' '}
                  <span className="text-slate-700 font-medium">
                    &ldquo;{stats.top[0].message.slice(0, 70)}{stats.top[0].message.length > 70 ? '…' : ''}&rdquo;
                  </span>{' '}
                  ×{stats.top[0].count}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={refresh}
              disabled={loading}
              className="h-8 rounded-full gap-1 text-xs"
              data-testid="system-health-rescan-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {!healthy && !loading && (
              <Button
                size="sm"
                onClick={openDrill}
                className={`h-8 rounded-full gap-1 text-xs ${critical ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                data-testid="system-health-view-details-btn"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View details
              </Button>
            )}
          </div>
        </div>
      </div>

      {drillOpen && (
        <div
          data-testid="system-health-drill"
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          onClick={() => setDrillOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                  Self-Detected Errors
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Last 50 entries · Newest first · Auto-emailed to developer
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => setDrillOpen(false)}
                data-testid="system-health-drill-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {drillLoading ? (
                <div className="flex items-center justify-center py-20 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
              ) : entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <CheckCircle2 className="h-10 w-10 mb-3 text-emerald-300" />
                  <p className="text-sm">No errors recorded.</p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {entries.map((e, i) => (
                    <li key={`${e.fingerprint || 'entry'}-${e.received_at || i}`} className="px-5 py-3.5 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">
                              {e.source}
                            </span>
                            {e.emailed_at ? (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                                emailed
                              </span>
                            ) : e.email_error === 'throttled' ? (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                                throttled
                              </span>
                            ) : (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">
                                send-failed
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400 font-mono">
                              {e.fingerprint?.slice(0, 12)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-800 font-medium leading-snug">
                            {e.message?.slice(0, 240) || '(no message)'}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap mt-1.5 text-[11px] text-slate-500">
                            <span>{new Date(e.received_at).toLocaleString()}</span>
                            {e.url && <span className="font-mono">· {prettyUrl(e.url)}</span>}
                            {e.user_email && <span>· {e.user_email}</span>}
                            {e.status_code && <span>· status {e.status_code}</span>}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
              Errors are PHI-redacted before email and at-rest storage. Identical bugs are de-duplicated
              for 30 minutes per fingerprint to prevent flood.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
